/**
 * How old a deal is — the one reading of each clock, and the one stage-aging rule.
 *
 * Two clocks, never confused (register E5): `daysInStatus` is the time in the stage the deal is in now,
 * `totalDaysOpen` the cumulative age since it entered the system. Both are computed by the API from the
 * append-only stage history. The stored `daysOpen` column is written as 0 at intake and never moved, so
 * nothing here reads it: a rule that did flagged no deal, and a column that did printed "0d" on all of them.
 *
 * The server's twin is `app.underwriting.stage_dwell.stage_aging_exceeded`; `STAGE_AGING_DAYS` is held to its
 * `STAGE_AGING_DAYS` by `backend/tests/test_stage_aging_rule.py`.
 */

import { LOAN_STAGES } from '@/types'

/** Days in one stage past which a deal is flagged "Stage aging above 60 days". */
export const STAGE_AGING_DAYS = 60

/** The final stage of the origination model — the one a deal does not age in. */
const FUNDED_STAGE = LOAN_STAGES[LOAN_STAGES.length - 1]

type Aged = { daysInStatus?: number | null; stage?: string | null }

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

/** The deal's cumulative age in days, or null where it cannot be told (never the stored `daysOpen`). */
export function totalAgeDays(loan: { totalDaysOpen?: number | null }): number | null {
  return finite(loan.totalDaysOpen) ? loan.totalDaysOpen : null
}

/** Whether the deal has sat in its stage past `STAGE_AGING_DAYS`. A funded deal does not age, and a dwell
 *  that could not be measured flags nothing: an unknown age is not an old one. */
export function stageAgingExceeded(loan: Aged): boolean {
  return finite(loan.daysInStatus) && loan.daysInStatus > STAGE_AGING_DAYS && loan.stage !== FUNDED_STAGE
}
